// Central analytics service (singleton) over Firebase Analytics (GA4).
//
// - Queues events until GA is ready (analytics inits async; null in unsupported WebViews).
// - Auto-enriches every event with a common envelope (timestamp, platform, app version,
//   session id, logged-in state, user id/role).
// - PII-safe: phone/email are never sent raw — only masked (last 4 of phone, first 4 of email).
// - Naming: callers use GA4 standard event names where they apply (page_view, view_item,
//   view_item_list, add_to_cart, begin_checkout, add_payment_info, purchase) and custom
//   snake_case names elsewhere. See EVENTS below.
// - Native de-dup: pass { nativeLogged: true } for events the native app ALSO logs to the
//   same GA property — inside the WebView we skip the web send to avoid double counting.

import { logEvent as gaLogEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsReady } from '../firebase';
import { isTossitNativeApp } from './location';
import { getAnalyticsSession } from './nativeBridge';

// Canonical event-name registry. GA4 standard names where applicable, custom otherwise.
// Reserved GA4/Firebase names renamed: app_open -> app_launch, session_start -> app_session_start.
export const EVENTS = {
    // --- GA4 standard ---
    PAGE_VIEW: 'page_view',
    LOGIN: 'login',
    SIGN_UP: 'sign_up',
    SELECT_ITEM: 'select_item',            // dish_click
    SEARCH: 'search',                      // search_performed
    VIEW_ITEM: 'view_item',                // (next) dish detail open
    VIEW_ITEM_LIST: 'view_item_list',      // catalogue impressions
    VIEW_CART: 'view_cart',                // (next) session review open
    ADD_TO_CART: 'add_to_cart',            // add_to_session
    REMOVE_FROM_CART: 'remove_from_cart',  // (next) remove/decrease qty
    BEGIN_CHECKOUT: 'begin_checkout',      // payment_initiate
    ADD_PAYMENT_INFO: 'add_payment_info',  // payment_method_default_view
    PURCHASE: 'purchase',                  // booking_confirmed
    // --- custom: auth ---
    PHONE_NUMBER_SUBMIT: 'phone_number_submit',
    OTP_SUBMIT: 'otp_submit',
    NAME_SUBMIT: 'name_submit',
    NON_SERVICEABLE_ADDRESS_VIEW: 'non_serviceable_address_view',
    // --- custom: app & navigation ---
    APP_LAUNCH: 'app_launch',              // renamed from reserved app_open
    COLLECTION_CLICK: 'collection_click',
    SUB_COLLECTION_CLICK: 'sub_collection_click',
    FILTER_CLICK: 'filter_click',
    SEARCH_CLICK: 'search_click',
    // --- custom: customization ---
    ADD_CLICK: 'add_click',
    PORTION_SIZE_CLICK: 'portion_size_click',
    ADDON_CLICK: 'addon_click',
    CUSTOMIZATION_CLICK: 'customization_click',       // opens customization UI
    VIEW_INGREDIENTS_CLICK: 'view_ingredients_click',
    CUSTOMIZATION_SELECT: 'customization_select',     // an option is chosen
    // --- custom: checkout ---
    TIME_SLOT_SELECT: 'time_slot_select',
    PAYMENT_METHOD_CHANGE: 'payment_method_change',
    // --- custom: post-booking ---
    ADD_DISH_POST_BOOKING_CLICK: 'add_dish_post_booking_click',
    // --- custom: session ---
    APP_SESSION_START: 'app_session_start',           // renamed from reserved session_start
    SESSION_ENGAGED: 'session_engaged',
    // --- custom: /app download landing ---
    APP_LANDING_VIEW: 'app_landing_view',             // /app page load (carries utm_source, ad_id)
    // --- custom: misc UI interactions ---
    VEG_TOGGLE: 'veg_toggle',                         // dashboard veg-only toggle (checked bool)
    ADD_MORE_DISHES: 'add_more_dishes',               // order details → add more dishes
    CANCEL_BOOKING: 'cancel_booking',                 // order details → open cancel flow
    RESCHEDULE_BOOKING: 'reschedule_booking',         // order details → open reschedule flow
    KEEP_ORDER: 'keep_order',                         // cancel modal → dismissed (kept order)
    CONFIRM_CANCELLATION: 'confirm_cancellation',     // cancel modal → confirmed cancellation
    COUPON_CLICK: 'coupon_click',                     // checkout → coupon field focused
    COUPON_ADD: 'coupon_add',                         // checkout → coupon apply attempt
    // --- native-origin (routed into the web stream via the bridge) ---
    PUSH_OPEN: 'push_open',                            // push-notification tap (native)
    UPI_RESULT: 'upi_result',                          // native UPI payment result
};

// Key events that flip a session to "engaged" (session_engaged.trigger_type = 'key_event').
export const SESSION_ENGAGED_KEY_EVENTS = [
    EVENTS.ADD_TO_CART, EVENTS.PORTION_SIZE_CLICK, EVENTS.ADDON_CLICK, EVENTS.CUSTOMIZATION_SELECT,
];

// Currency is always INR — defaulted onto money events when the caller omits it.
export const DEFAULT_CURRENCY = 'INR';
const MONEY_EVENTS = new Set([
    EVENTS.ADD_TO_CART, EVENTS.REMOVE_FROM_CART, EVENTS.BEGIN_CHECKOUT,
    EVENTS.ADD_PAYMENT_INFO, EVENTS.PURCHASE, EVENTS.VIEW_ITEM, EVENTS.VIEW_CART,
]);

// ---- PII masking (exported so screens can include masked values in params) ----
export const maskPhone = (phone) => {
    const d = String(phone || '').replace(/\D/g, '');
    return d ? `••••${d.slice(-4)}` : undefined;
};
export const maskEmail = (email) => {
    const s = String(email || '');
    return s ? `${s.slice(0, 4)}••••` : undefined;
};

// ---- session id ----
// Inside the native app we DEFER to the session the native GA4 SDK owns (injected via the
// bridge) so web + native events stitch into ONE session. On plain web, we mint/reuse a
// per-tab session id in sessionStorage.
const SESSION_KEY = 'tossit_analytics_session';
const getWebSessionId = () => {
    try {
        let s = sessionStorage.getItem(SESSION_KEY);
        if (!s) { s = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; sessionStorage.setItem(SESSION_KEY, s); }
        return s;
    } catch { return 'no-session'; }
};
// Returns { session_id, session_number? } — the native-owned session if the host injected
// one (via window.tossitAnalyticsSession or the getter), regardless of UA/bridge detection;
// otherwise the web's own per-tab session id.
const sessionInfo = () => {
    const ns = getAnalyticsSession();
    if (ns && ns.session_id) return ns;
    return { session_id: getWebSessionId() };
};

// ---- debug mode (secret-driven; preview only) ----
// GA4 DebugView routing is OFF by default. It turns ON only when the caller presents the
// secret from REACT_APP_ANALYTICS_DEBUG_SECRET — e.g. opening `?ga_debug=<secret>` once.
// The unlock is remembered for the browser session (sessionStorage); `?ga_debug=off` clears
// it. Because the secret lives only in the preview env (unset in production), prod can never
// be toggled into debug. When ON, every event carries debug_mode:true → appears in DebugView.
let _debugMode = false;
export const resolveAnalyticsDebug = () => {
    try {
        const secret = process.env.REACT_APP_ANALYTICS_DEBUG_SECRET;
        if (!secret) { _debugMode = false; return _debugMode; }
        const provided = new URLSearchParams(window.location.search).get('ga_debug');
        if (provided === 'off') { sessionStorage.removeItem('ga_debug'); _debugMode = false; return _debugMode; }
        if (provided && provided === secret) sessionStorage.setItem('ga_debug', '1');
        _debugMode = sessionStorage.getItem('ga_debug') === '1';
    } catch { _debugMode = false; }
    return _debugMode;
};
export const isAnalyticsDebug = () => _debugMode;
resolveAnalyticsDebug();

// ---- module state ----
let _instance = null;   // resolved GA instance or null (unsupported)
let _ready = false;
let _user = null;       // { id, role }
const _queue = [];
const _seenImpressions = new Set();  // per-session dedupe: `${list}:${item_id}`
let _impBuffer = [];
let _impTimer = null;
let _impListName = 'catalogue';

analyticsReady.then((a) => {
    _instance = a || null;
    _ready = true;
    applyUser();
    // flush anything queued before GA was ready
    while (_queue.length) {
        const { name, params } = _queue.shift();
        _emit(name, params);
    }
});

// ---- helpers ----
const GA4_STR_MAX = 100;
const sanitize = (params) => {
    const out = {};
    let count = 0;
    for (const [k, v] of Object.entries(params || {})) {
        if (v === undefined || v === null) continue;
        if (count >= 25) break; // GA4: max 25 params/event
        if (typeof v === 'string') out[k] = v.length > GA4_STR_MAX ? v.slice(0, GA4_STR_MAX) : v;
        else if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
        else if (Array.isArray(v)) out[k] = v; // e.g. items[] for view_item_list
        else out[k] = v;
        count++;
    }
    return out;
};

const envelope = (extra) => {
    const s = sessionInfo();
    return {
        event_ts: Date.now(),
        event_time_iso: new Date().toISOString(),
        platform: isTossitNativeApp() ? 'native_webview' : 'web',
        app_version: process.env.REACT_APP_VERSION || 'unknown',
        // NOTE: `session_id`/`session_number` are GA4 RESERVED params that Firebase strips
        // before sending — so we ship our (possibly native-owned) session under non-reserved
        // keys. This is what stitches web + native into ONE session on the wire.
        analytics_session_id: s.session_id,
        ...(s.session_number != null ? { analytics_session_number: s.session_number } : {}),
        logged_in: !!_user,
        // Identity: customer_id + MASKED phone only — never raw phone/name/email.
        ...(_user ? {
            user_id: _user.id,
            customer_id: _user.id,
            user_role: _user.role,
            ...(_user.phone_masked ? { phone_last4: _user.phone_masked } : {}),
        } : {}),
        ...extra,
    };
};

const _emit = (name, params) => {
    if (!_instance) return; // unsupported env → drop
    const out = _debugMode ? { ...params, debug_mode: true } : params; // route to GA4 DebugView
    try { gaLogEvent(_instance, name, sanitize(out)); } catch { /* never break UI */ }
};

const _send = (name, params) => {
    const payload = envelope(params);
    if (!_ready) { _queue.push({ name, params: payload }); return; }
    _emit(name, payload);
};

const applyUser = () => {
    if (!_instance || !_user) return;
    try {
        if (_user.id) setUserId(_instance, _user.id);
        setUserProperties(_instance, {
            user_role: _user.role || 'customer',
            ...(_user.phone_masked ? { phone_last4: _user.phone_masked } : {}),
            ...(_user.email_masked ? { email_first4: _user.email_masked } : {}),
        });
    } catch { /* noop */ }
};

// ---- public API ----
export const setAnalyticsUser = (user) => {
    if (!user) { _user = null; return; }
    _user = {
        id: user.id,
        role: user.role || 'customer',
        phone_masked: maskPhone(user.phone),
        email_masked: maskEmail(user.email),
    };
    applyUser();
};

export const clearAnalyticsUser = () => { _user = null; };

// ---- single cross-platform session (native owns it) ----
// Native calls window.tossitSetAnalyticsSession({session_id, session_number}) on load and on
// every GA4 session refresh; we store it so the envelope stamps this EXACT session on every
// web event → web + native share ONE session id. (getAnalyticsSession reads it back.)
export const setAnalyticsSession = (session) => {
    try {
        if (session && session.session_id) {
            window.tossitAnalyticsSession = {
                session_id: String(session.session_id),
                ...(session.session_number != null ? { session_number: session.session_number } : {}),
            };
        } else if (typeof window !== 'undefined') {
            delete window.tossitAnalyticsSession;
        }
    } catch (e) { /* noop */ }
};

// Log an event that ORIGINATED in the native app (app launch, push tap, UPI result) INTO the
// web GA stream. Always sent (not double-logged elsewhere) and tagged with event_origin.
export const trackNativeEvent = (name, params = {}) => {
    if (!name) return;
    _send(name, { ...params, event_origin: 'native' });
};

// Register the window globals the native host calls to hand down its GA4 session and route
// native-origin analytics events into the web stream. Call ONCE at app start.
export const initNativeAnalyticsBridge = () => {
    if (typeof window === 'undefined') return;
    window.tossitSetAnalyticsSession = (session) => setAnalyticsSession(session);
    window.tossitTrackEvent = (name, params) => trackNativeEvent(name, params || {});
    window.tossitOnAppLaunch = (params) => trackNativeEvent(EVENTS.APP_LAUNCH, params || {});
    window.tossitOnAppSessionStart = (params) => trackNativeEvent(EVENTS.APP_SESSION_START, params || {});
    window.tossitOnPushOpen = (params) => trackNativeEvent(EVENTS.PUSH_OPEN, params || {});
    window.tossitOnUpiResult = (params) => trackNativeEvent(EVENTS.UPI_RESULT, params || {});
};

// ---- session lifecycle & engagement ----
let _sessionStarted = false;
let _engaged = false;
let _pageViews = 0;
let _engageTimer = null;

const markEngaged = (trigger_type, key_event_name) => {
    if (_engaged) return;
    _engaged = true;
    if (_engageTimer) { clearTimeout(_engageTimer); _engageTimer = null; }
    _send(EVENTS.SESSION_ENGAGED, { trigger_type, ...(key_event_name ? { key_event_name } : {}) });
};

// Fire app_session_start once per app load. Native emits it itself via the bridge, so on
// web only the plain browser owns it here. Also arms the 10s-dwell → session_engaged timer.
export const ensureAppSessionStart = (entry_point) => {
    if (_sessionStarted) return;
    _sessionStarted = true;
    if (!isTossitNativeApp()) {
        _send(EVENTS.APP_SESSION_START, {
            entry_point: entry_point || (typeof window !== 'undefined' ? window.location.pathname : undefined),
        });
    }
    _engageTimer = setTimeout(() => markEngaged('10s'), 10000);
};

/**
 * Generic event. Pass any custom params; the common envelope is merged automatically.
 * opts.nativeLogged=true → skip the web send inside the native WebView (native SDK logs it).
 */
export const trackEvent = (name, params = {}, opts = {}) => {
    if (!name) return;
    if (opts.nativeLogged && isTossitNativeApp()) return; // avoid double-count with native SDK
    // Currency is always INR — default it onto money events if the caller omitted it.
    const finalParams = (MONEY_EVENTS.has(name) && params.currency === undefined)
        ? { ...params, currency: DEFAULT_CURRENCY }
        : params;
    _send(name, finalParams);
    // A key event flips the session to "engaged" (once).
    if (SESSION_ENGAGED_KEY_EVENTS.includes(name)) markEngaged('key_event', name);
};

export const trackPageView = (path, params = {}) => {
    const p = path || (typeof window !== 'undefined' ? window.location.pathname : undefined);
    _send(EVENTS.PAGE_VIEW, {
        page_path: p,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        page_title: typeof document !== 'undefined' ? document.title : undefined,
        ...params,
    });
    // Two page views in a session → engaged.
    _pageViews += 1;
    if (_pageViews >= 2) markEngaged('2_pageviews');
};

const normalizeItem = (it, index) => ({
    item_id: it.item_id || it.id,
    item_name: it.item_name || it.name,
    item_category: it.item_category || it.category,
    price: it.price,
    index: it.index ?? index,
});

/**
 * Catalogue impressions → GA4 `view_item_list`. De-duped once per session per (list, item)
 * and batched over 500ms so a scrolling list produces one event, not hundreds.
 */
export const trackImpression = (items, { list_name } = {}) => {
    const arr = Array.isArray(items) ? items : [items];
    if (list_name) _impListName = list_name;
    arr.forEach((it) => {
        const id = it.item_id || it.id;
        if (!id) return;
        const key = `${list_name || _impListName}:${id}`;
        if (_seenImpressions.has(key)) return;
        _seenImpressions.add(key);
        _impBuffer.push(normalizeItem(it, _impBuffer.length));
    });
    if (!_impBuffer.length) return;
    if (_impTimer) clearTimeout(_impTimer);
    _impTimer = setTimeout(() => {
        const batch = _impBuffer.splice(0);
        _impTimer = null;
        _send(EVENTS.VIEW_ITEM_LIST, { item_list_name: _impListName, items: batch });
    }, 500);
};
